/**
 * Obras de pagamento faseado com uma fase por pagar fora do prazo.
 *
 * Não suspende nada e não cancela nada, de propósito. Do outro lado há uma
 * equipa parada e materiais já comprados, e em Luanda um atraso de pagamento
 * raramente significa desistência — suspender por temporizador fazia mais
 * estragos do que resolvia.
 *
 * O que uma fase atrasada precisa é de um telefonema, por isso é isso que
 * este ecrã serve: os dois números lado a lado, o valor em falta, e o que a
 * empresa já fez e não pode receber enquanto aquilo não entrar. Quem liga
 * precisa de ouvir as duas versões.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Clock, Phone } from 'lucide-react';
import api from '../../utils/api';

const kz = (v) => `${Number(v || 0).toLocaleString('pt-PT')} Kz`;
const dt = (v) => (v ? new Date(v).toLocaleDateString('pt-PT', { timeZone: 'Africa/Luanda' }) : '—');

const FasesEmAtraso = () => {
  const [lista, setLista] = useState(null);

  const carregar = useCallback(() => {
    api.get('/projects/fases/vencidas')
      .then((r) => setLista(r.data || []))
      .catch(() => setLista([]));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  if (!lista) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-600" /></div>;
  }

  const total = lista.reduce((s, x) => s + Number(x.em_falta_kz || 0), 0);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Clock className="w-6 h-6 text-amber-400" /> Fases de pagamento em atraso
        </h1>
        <p className="text-sm text-slate-400 mt-1 max-w-2xl">
          Obras de pagamento faseado onde a fase seguinte passou do prazo. A obra
          não para sozinha — mas as etapas que não estão cobertas não podem ser
          libertadas, e do outro lado há uma equipa à espera.
        </p>
      </div>

      {lista.length === 0 ? (
        <div className="p-6 rounded-xl border border-slate-800 bg-slate-900 text-center text-slate-400 text-sm">
          Nenhuma fase em atraso.
        </div>
      ) : (
        <>
          <div className="p-3 rounded-xl border border-amber-500/30 bg-amber-500/10 text-sm text-amber-200">
            <strong className="tabular-nums">{kz(total)}</strong> por entrar em{' '}
            {lista.length} {lista.length === 1 ? 'obra' : 'obras'}.
          </div>

          <ul className="space-y-2">
            {lista.map((x) => (
              <li key={x.contrato_id} className="p-4 rounded-xl border border-slate-800 bg-slate-900 space-y-2">
                <div className="flex gap-3 items-start flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <div className="font-semibold text-slate-100">{x.titulo}</div>
                    <div className="text-xs text-slate-500 font-mono">
                      {x.codigo} · {x.numero}
                      {x.escrow_fase_referencia && <> · ref {x.escrow_fase_referencia}</>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-amber-400 font-semibold tabular-nums">{kz(x.em_falta_kz)}</div>
                    <div className="text-[11px] text-slate-500">
                      {x.dias_atraso} {x.dias_atraso === 1 ? 'dia' : 'dias'} de atraso
                      {' · '}venceu a {dt(x.escrow_fase_pagar_ate)}
                    </div>
                  </div>
                </div>

                <div className="text-xs text-slate-400">
                  Protegido {kz(x.escrow_pago_kz)} de {kz(x.escrow_kz)} · cobre até à etapa{' '}
                  {x.coberto_ate_ordem || 'nenhuma'}
                </div>

                <div className="grid gap-2 sm:grid-cols-2 pt-2 border-t border-slate-800">
                  {[[x.cliente, x.cliente_telefone, 'Cliente'],
                    [x.empresa, x.empresa_telefone, 'Empresa']].map(([nome, tel, papel]) => (
                    <div key={papel} className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] text-slate-500 uppercase tracking-wide">{papel}</div>
                        <div className="text-sm text-slate-200 truncate">{nome || '—'}</div>
                      </div>
                      {tel && (
                        <a href={`tel:${tel}`}
                          className="min-h-[40px] px-3 rounded-lg bg-slate-800 text-slate-200 text-xs
                                     inline-flex items-center gap-1.5">
                          <Phone className="w-3.5 h-3.5" /> Ligar
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default FasesEmAtraso;
